import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const INITIAL_MESSAGES = [
  {
    id: 'welcome',
    type: 'bot',
    text: "Hi! I'm the QIndex One support assistant. How can I help you today?",
    options: [
      "How do I add a new client?",
      "How do I log a call?",
      "How do I create a task?",
      "I need help with something else"
    ]
  }
];

const FAQ_RESPONSES = {
  "How do I add a new client?": {
    text: "To add a new client:\n\n1. Go to the Family Groups dashboard\n2. Click the 'Add Client' button in the top right\n3. Fill in the client name, segment, and relationship lead\n4. Click 'Create Client'\n\nYou can then add individuals, entities, and other details from the client overview page.",
    followUp: ["How do I add individuals?", "How do I add entities?", "Back to main menu"]
  },
  "How do I log a call?": {
    text: "To log a call:\n\n1. Navigate to any client's overview page\n2. Find the 'Call Log' section\n3. Click '+ Add' to create a new call log\n4. Fill in the call type, participants, date/time, and summary\n5. Click 'Save Call Log'\n\nThe call will be recorded in the Activity feed automatically.",
    followUp: ["How do I send a call summary?", "Where can I see all calls?", "Back to main menu"]
  },
  "How do I create a task?": {
    text: "To create a task:\n\n1. Go to any client's 'Workflow & Key Dates' section\n2. Click '+ Add Task'\n3. Fill in the title, due date, owner, and status\n4. Optionally link it to an entity or individual\n5. Click 'Save Task'\n\nWhen a task is completed, it will be logged in the Activity feed.",
    followUp: ["How do recurring tasks work?", "How do I complete a task?", "Back to main menu"]
  },
  "I need help with something else": {
    text: "I'm here to help! You can:\n\n• Browse our documentation\n• Contact our support team at support@qindexone.com\n• Request a walkthrough session\n\nWhat would you like to do?",
    followUp: ["Contact support", "Request walkthrough", "Back to main menu"]
  },
  "How do I add individuals?": {
    text: "To add an individual:\n\n1. Go to the client's 'Individuals' tab\n2. Click '+ Add Individual'\n3. Fill in their name, role, and contact details\n4. Click 'Save'\n\nYou can then add notes and call logs specific to that individual.",
    followUp: ["How do I add entities?", "Back to main menu"]
  },
  "How do I add entities?": {
    text: "To add an entity:\n\n1. Go to the client's 'Entities & Roles' tab\n2. Click '+ Add Entity'\n3. Fill in the entity name, type (Trust, Company, etc.), and status\n4. Click 'Save'\n\nYou can link documents, tasks, and individuals to entities.",
    followUp: ["How do I add individuals?", "Back to main menu"]
  },
  "How do I send a call summary?": {
    text: "To send a call summary via email:\n\n1. Find the call log you want to share\n2. Click the 'Send Summary' button\n3. Enter the recipient's email address\n4. Click Send\n\nThe recipient will receive a formatted summary of the call.",
    followUp: ["How do I log a call?", "Back to main menu"]
  },
  "Where can I see all calls?": {
    text: "You can see all calls in the 'Activity' tab of any client. This shows all logged calls along with other activity like task completions and record changes.",
    followUp: ["How do I log a call?", "Back to main menu"]
  },
  "How do recurring tasks work?": {
    text: "When creating a task, you can mark it as recurring and set a frequency (weekly, monthly, quarterly, annually). When you complete a recurring task, a new task is automatically created with the next due date.",
    followUp: ["How do I create a task?", "Back to main menu"]
  },
  "How do I complete a task?": {
    text: "To complete a task:\n\n1. Find the task in the 'Workflow & Key Dates' section\n2. Click to edit the task\n3. Change the status to 'Completed'\n4. Click Save\n\nCompleted tasks are archived and logged in Activity.",
    followUp: ["How do I create a task?", "Back to main menu"]
  },
  "Contact support": {
    text: "You can reach our support team at:\n\n📧 Email: support@qindexone.com\n📞 Phone: +61 2 1234 5678\n\nOur team typically responds within 4 business hours.",
    followUp: ["Request walkthrough", "Back to main menu"]
  },
  "Request walkthrough": {
    text: "To request a walkthrough session, visit our contact page or email support@qindexone.com with your preferred times. Our team will arrange a personalized demo.",
    followUp: ["Contact support", "Back to main menu"]
  },
  "Back to main menu": {
    text: "Sure! What would you like help with?",
    options: [
      "How do I add a new client?",
      "How do I log a call?",
      "How do I create a task?",
      "I need help with something else"
    ]
  }
};

export default function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOptionClick = (option) => {
    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      text: option
    };
    setMessages(prev => [...prev, userMessage]);

    // Show typing indicator
    setIsTyping(true);

    // Get response after delay
    setTimeout(() => {
      setIsTyping(false);
      const response = FAQ_RESPONSES[option];
      if (response) {
        const botMessage = {
          id: `bot-${Date.now()}`,
          type: 'bot',
          text: response.text,
          options: response.options || response.followUp
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const botMessage = {
          id: `bot-${Date.now()}`,
          type: 'bot',
          text: "I'm not sure about that. Would you like to contact our support team?",
          options: ["Contact support", "Back to main menu"]
        };
        setMessages(prev => [...prev, botMessage]);
      }
    }, 800);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      text: inputValue
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Show typing indicator
    setIsTyping(true);

    // Generic response for free-form input
    setTimeout(() => {
      setIsTyping(false);
      const botMessage = {
        id: `bot-${Date.now()}`,
        type: 'bot',
        text: "Thanks for your message! For specific questions, our support team can help. Would you like me to connect you?",
        options: ["Contact support", "Back to main menu"]
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  const resetChat = () => {
    setMessages(INITIAL_MESSAGES);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 md:bottom-6 right-24 w-14 h-14 bg-[#A86A3A] text-white rounded-full shadow-lg hover:bg-[#8B5730] transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
        data-testid="support-chat-btn"
        aria-label="Open support chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-24 md:bottom-6 right-2 md:right-24 w-[min(24rem,calc(100vw-1rem))] bg-white rounded-xl shadow-2xl z-50 overflow-hidden transition-all duration-300 ${isMinimized ? 'h-14' : 'h-[500px]'}`}
      data-testid="support-chat-window"
    >
      {/* Header */}
      <div className="bg-[#A86A3A] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-medium text-sm">QIndex Support</h3>
            <p className="text-xs text-white/70">Online • Typically replies instantly</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setIsOpen(false); resetChat(); }}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-[380px] overflow-y-auto p-4 space-y-4 bg-[#FAF8F5]">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                  {message.type === 'bot' && (
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-[#A86A3A] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm">
                          <p className="text-sm text-[#1A1411] whitespace-pre-line">{message.text}</p>
                        </div>
                        {message.options && (
                          <div className="mt-2 space-y-1">
                            {message.options.map((option, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleOptionClick(option)}
                                className="block w-full text-left text-sm px-3 py-2 bg-white border border-[#E8E4DF] rounded-lg hover:border-[#A86A3A] hover:text-[#A86A3A] transition-colors"
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {message.type === 'user' && (
                    <div className="flex items-start gap-2 justify-end">
                      <div className="bg-[#A86A3A] text-white rounded-lg rounded-tr-none p-3">
                        <p className="text-sm">{message.text}</p>
                      </div>
                      <div className="w-6 h-6 bg-[#E8E4DF] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-3 h-3 text-[#6B5B52]" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 bg-[#A86A3A] rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3 h-3 text-white" />
                </div>
                <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#A86A3A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#A86A3A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#A86A3A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-3 border-t border-[#E8E4DF] bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 text-sm border border-[#E8E4DF] rounded-lg focus:outline-none focus:border-[#A86A3A]"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!inputValue.trim()}
                className="bg-[#A86A3A] hover:bg-[#8B5730] text-white px-3 py-2 rounded-lg disabled:opacity-50"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
